import AdminHeader from "components/Headers/AdminHeader";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Input,
  Tooltip,
  UncontrolledTooltip,
  CardFooter,
  Badge,
} from "reactstrap";
import { toCamelCase } from "components/CommonComponents/CommonFunctions";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { useDispatch, useSelector } from "react-redux";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";
import { getInstructorCourseList } from "action/instructor";
import { instructorCourseList } from "constants/instructor";
import {
  BATCH_STATUS_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
  STATUS_BADGE_COLORS,
} from "components/constant";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";

const InstructorCourses = () => {
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const isFirstSearchRender = useRef(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMyCourse = pathname.includes("/my-courses");
  const mode = isMyCourse ? "MY_COURSE" : "COURSE";

  const [courseListState, setCourseListState] = useState({
    isLoading: true,
    isSearched: false,
    error: "",
    noRecords: false,
  });
  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    searchName: "",
    filterParams: {
      searchString: "",
    },
  });
  // const [isLiveChecked, setIsLiveChecked] = useState(true);
  const [tooltipOpen, setTooltipOpen] = useState({});
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [copiedLink, setCopiedLink] = useState(null);

  const courseListData = useSelector(
    (state) => state.instructor.instructorCourseList,
  );

  useEffect(() => {
    setCourseListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    getInstructorCourseList(dispatch, pageNum, debouncedSearch);

    return () => {
      dispatch({ type: instructorCourseList, payload: {} });
    };
  }, [pageNum, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterState.searchName);
    }, 500);

    return () => clearTimeout(timer);
  }, [filterState.searchName]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    // if (!filterState.searchName) {
    //   return;
    // }

    setPageNum(1);

    setFilterState((prev) => {
      const updatedState = {
        ...prev,
        searchName: debouncedSearch,
      };

      return {
        ...updatedState,
      };
    });
  }, [debouncedSearch]);

  useEffect(() => {
    if (courseListData?.code === 200) {
      setCourseListState({
        isLoading: false,
        error: "",
        noRecords: courseListData?.data?.data?.length === 0,
      });
    } else if (courseListData?.code || courseListData?.message) {
      setCourseListState({
        isLoading: false,
        error:
          courseListData?.message ||
          "Failed to load course list! Please try again.",
        noRecords: false,
      });
    }
  }, [courseListData]);

  const courses = courseListData?.data?.data || [];

  const totalRecords = courseListData?.data?.totalRecords || 0;

  const totalFilteredRecords = courseListData?.data?.filteredRecords || 0;

  const totalPages = Math.ceil(
    totalFilteredRecords /
      (courseListData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE),
  );

  const toggleTooltip = (key) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;

    setFilterState((prev) => ({
      ...prev,
      searchName: value,
    }));
  };

  const handleNavigation = (courseId, navigateBatch = false) => {
    if (navigateBatch) {
      navigate(
        `/instructor/my-courses/${urlEncodedId(courseId)}/details?tab=batches`,
      );
    } else {
      const targetPath = isMyCourse
        ? `/instructor/my-courses/${urlEncodedId(courseId)}/details`
        : `/instructor/course/${urlEncodedId(courseId)}/details`;

      navigate(targetPath, { state: { mode: mode } });
    }
  };

  const handleCopyEnrollmentLink = async (url, batchId, type) => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);

      setCopiedLink({
        batchId,
        type,
      });

      setTimeout(() => {
        setCopiedLink(null);
      }, 2000);
    } catch (error) {
      notify("warning", "Failed to copy enrollment link");
    }
  };

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6 mb-4">
        <div className="d-flex align-items-center mb-4">
          <div className="d-flex align-items-center flex-shrink-0">
            <h3 className="mb-0 mt-4 text-white-input flex-shrink-0">
              Courses
            </h3>
            {!courseListState.isLoading && (
              <>
                <span className="text-white-input mb-0 mt-4 ml-2 mr-2">|</span>
                <span className="text-white-input mb-0 mt-4">
                  {debouncedSearch
                    ? `Showing ${totalFilteredRecords} of ${totalRecords} Records`
                    : `Total Records: ${totalRecords}`}
                </span>
              </>
            )}
          </div>

          <div className="d-flex align-items-center ml-auto">
            <InputGroup className="mr-4 flex-grow-1 search-bottom-border">
              <Input
                type="text"
                placeholder="Search title, category"
                className="text-white-input"
                value={filterState.searchName}
                onChange={handleSearchChange}
              />

              <InputGroupAddon addonType="append">
                <InputGroupText className="search-icons">
                  {filterState.searchName && (
                    <i
                      className="fa fa-times clear-search"
                      onClick={() =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: "",
                        }))
                      }
                    />
                  )}

                  <i className="fa fa-search" />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </div>
        </div>

        {/* <div className="d-lg-none d-flex justify-content-end mt-5 mb-1 w-100">
                    <Button
                        color="primary"
                        className="btn-deep-blue"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                    </Button>
                </div> */}

        <Row className="mt-5">
          {/* left card gird for courses */}
          <Col xl="12" lg="12" md="12">
            {courseListState.isLoading ? (
              <div className="loaderContainer no-courses-container">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : courseListState.error ? (
              <div className="loaderContainer error-style no-courses-container">
                <div className="error-icon">
                  <i class="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{courseListState.error}</span>
              </div>
            ) : courseListState.noRecords ? (
              <div className="loaderContainer empty-style no-courses-container">
                <div className="empty-icon">
                  <i class="fa-solid fa-circle-info"></i>
                </div>
                <span>No courses found.</span>
              </div>
            ) : (
              // <div className="no-courses-container">
              //   <div className="no-courses-icon">
              //     <i className="fa fa-folder-open" />
              //   </div>

              //   <h3 className="no-courses-title">No courses found</h3>
              // </div>
              <>
                <div className="instructor-courses-grid">
                  {courses.map((course) => {
                    const primaryBatch = course?.courseBatches[0];
                    const remainingBatchCount = Math.max(
                      course?.courseBatches?.length - 1,
                      0,
                    );

                    return (
                      <div
                        key={course.courseId}
                        className="course-card-wrapper"
                      >
                        <Card className="course-card mb-1">
                          {/* Course Image */}
                          <div
                            className="course-image-wrapper cursor"
                            onClick={() => handleNavigation(course.courseId)}
                          >
                            {course.mediaUrl?.match(
                              /\.(mp4|webm|ogg|mov|m4v)$/i,
                            ) ? (
                              <video
                                className="course-image"
                                src={course.mediaUrl}
                                muted
                                preload="metadata"
                                playsInline
                                disablePictureInPicture
                                controls={false}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <img
                                src={course.mediaUrl}
                                alt={course.courseName}
                                className="course-image"
                              />
                            )}

                            {/* <Badge
                              color={
                                STATUS_BADGE_COLORS[primaryBatch.batchStatus] ||
                                "secondary"
                              }
                              className="course-status-badge"
                            >
                              {BATCH_STATUS_OPTIONS.find(
                                (option) =>
                                  option.value === primaryBatch.batchStatus,
                              )?.label || primaryBatch.batchStatus}
                            </Badge> */}

                            {/* Course Type Badge */}
                            {/* {course.course_type && (
                                                            <span
                                                                className={`radio-badge image-radio-badge ${course.course_type === "LIVE"
                                                                    ? "live"
                                                                    : "recorded"
                                                                    }`}
                                                            >
                                                                <span className="radio-circle">
                                                                    <span className="radio-dot" />
                                                                </span>
                                                                {course.course_type}
                                                            </span>
                                                        )} */}

                            {/* <span className={`radio-badge image-radio-badge`}>
                                                            <span className="radio-circle">
                                                                <span className="radio-dot" />
                                                            </span>
                                                            {course.course_type}
                                                        </span> */}
                          </div>

                          {/* Course Details */}
                          <CardBody
                            className="pt-3 pb-1 ml--1 mr--1 cursor"
                            onClick={() => handleNavigation(course.courseId)}
                          >
                            {/* Course Title */}
                            <div className="small-text text-default font-weight-bold">
                              <div>
                                <div
                                  id={`courseTitleTooltip-${course.courseId}`}
                                  className="course-title-ellipsis"
                                >
                                  {course.courseName}
                                </div>

                                <Tooltip
                                  className="qb-tooltip"
                                  placement="auto"
                                  delay={{
                                    show: 150,
                                    hide: 0,
                                  }}
                                  target={`courseTitleTooltip-${course.courseId}`}
                                  isOpen={
                                    tooltipOpen[`title-${course.courseId}`]
                                  }
                                  toggle={() =>
                                    toggleTooltip(`title-${course.courseId}`)
                                  }
                                  autohide={false}
                                >
                                  {course.courseName}
                                </Tooltip>
                              </div>
                            </div>

                            <div className="d-flex justify-content-between mt-3">
                              <div>
                                <div className="text-muted small">Level</div>
                                <div className="xs-text">
                                  {toCamelCase(course.courseLevel)}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-muted small">Category</div>

                                <div>
                                  <span
                                    id={`courseCategoryTooltip-${course.courseId}`}
                                    className="course-category-ellipsis xs-text"
                                  >
                                    {course.courseCategory}
                                  </span>
                                </div>

                                <Tooltip
                                  className="qb-tooltip"
                                  placement="auto"
                                  delay={{
                                    show: 150,
                                    hide: 0,
                                  }}
                                  isOpen={
                                    tooltipOpen[`category-${course.courseId}`]
                                  }
                                  target={`courseCategoryTooltip-${course.courseId}`}
                                  toggle={() =>
                                    setTooltipOpen((prev) => ({
                                      ...prev,
                                      [`category-${course.courseId}`]:
                                        !prev[`category-${course.courseId}`],
                                    }))
                                  }
                                  autohide={false}
                                >
                                  {course.courseCategory}
                                </Tooltip>
                              </div>
                            </div>

                            {/* Assigned Batch */}
                            <div className="assigned-batch-section mt-3">
                              <div className="assigned-batch-divider" />
                              <div className="text-muted small">
                                Training Batch
                              </div>

                              <div className="d-flex align-items-start justify-content-between w-100">
                                <div className="w-100 overflow-hidden">
                                  <span
                                    id={`courseBatchTooltip-${course.courseId}`}
                                    className="xs-text d-inline-block text-truncate w-100"
                                  >
                                    {primaryBatch.batchName || "-"}
                                  </span>
                                </div>
                                <Tooltip
                                  className="qb-tooltip"
                                  placement="auto"
                                  delay={{
                                    show: 150,
                                    hide: 0,
                                  }}
                                  isOpen={
                                    tooltipOpen[`batch-${course.courseId}`]
                                  }
                                  target={`courseBatchTooltip-${course.courseId}`}
                                  toggle={() =>
                                    setTooltipOpen((prev) => ({
                                      ...prev,
                                      [`batch-${course.courseId}`]:
                                        !prev[`batch-${course.courseId}`],
                                    }))
                                  }
                                  autohide={false}
                                >
                                  {primaryBatch.batchName || "-"}
                                </Tooltip>
                              </div>

                              <div className="d-flex justify-content-between">
                                <div className="batch-meta-row mt-2 small w-100 d-flex align-items-center overflow-hidden">
                                  {(() => {
                                    const tcName =
                                      primaryBatch?.trainingCenterName;
                                    const vmUrl =
                                      primaryBatch?.virtualMeetingUrl;

                                    const virtualMeetingUI = (
                                      <a
                                        href={vmUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="virtual-meeting-link d-inline-flex align-items-center"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <i className="fa-solid fa-video mr-1" />
                                        Meeting Link
                                      </a>
                                    );

                                    if (tcName && vmUrl) {
                                      return (
                                        <div className="d-flex align-items-center w-100 overflow-hidden">
                                          <div className="training-center-wrapper w-50 pr-2 d-flex align-items-center overflow-hidden">
                                            <i className="fa fa-school mr-2 flex-shrink-0" />
                                            <span
                                              id={`trainingCenterTooltip-${course.courseId}`}
                                              className="training-center-ellipsis"
                                            >
                                              {tcName}
                                            </span>

                                            <Tooltip
                                              className="qb-tooltip"
                                              placement="auto"
                                              delay={{
                                                show: 150,
                                                hide: 0,
                                              }}
                                              isOpen={
                                                tooltipOpen[
                                                  `training-${course.courseId}`
                                                ]
                                              }
                                              target={`trainingCenterTooltip-${course.courseId}`}
                                              toggle={() =>
                                                setTooltipOpen((prev) => ({
                                                  ...prev,
                                                  [`training-${course.courseId}`]:
                                                    !prev[
                                                      `training-${course.courseId}`
                                                    ],
                                                }))
                                              }
                                              autohide={false}
                                            >
                                              {tcName}
                                            </Tooltip>
                                          </div>

                                          <div className="w-50">
                                            {virtualMeetingUI}
                                          </div>
                                        </div>
                                      );
                                    } else if (vmUrl && !tcName) {
                                      return virtualMeetingUI;
                                    } else if (tcName && !vmUrl) {
                                      return (
                                        <div className="d-flex align-items-center overflow-hidden">
                                          <i className="fa fa-school mr-2 flex-shrink-0" />
                                          <span
                                            id={`trainingCenterTooltip-${course.courseId}`}
                                            className="training-center-ellipsis"
                                          >
                                            {tcName}
                                          </span>

                                          <Tooltip
                                            className="qb-tooltip"
                                            placement="auto"
                                            delay={{
                                              show: 150,
                                              hide: 0,
                                            }}
                                            isOpen={
                                              tooltipOpen[
                                                `training-${course.courseId}`
                                              ]
                                            }
                                            target={`trainingCenterTooltip-${course.courseId}`}
                                            toggle={() =>
                                              setTooltipOpen((prev) => ({
                                                ...prev,
                                                [`training-${course.courseId}`]:
                                                  !prev[
                                                    `training-${course.courseId}`
                                                  ],
                                              }))
                                            }
                                            autohide={false}
                                          >
                                            {tcName}
                                          </Tooltip>
                                        </div>
                                      );
                                    } else {
                                      return null;
                                    }
                                  })()}
                                </div>
                              </div>

                              <div className="batch-meta-row mt-1 small">
                                <i className="fa fa-calendar mr-2" />
                                <span>
                                  {primaryBatch.batchStartDate} -{" "}
                                  {primaryBatch.batchEndDate}
                                </span>
                              </div>

                              <div className="batch-meta-row mt-1 small">
                                <i className="fa fa-clock mr-2" />
                                <span>
                                  {primaryBatch.batchStartTime} -{" "}
                                  {primaryBatch.batchEndTime}
                                </span>
                              </div>

                              <div className="batch-meta-row mt-1 small d-flex align-items-center justify-content-between">
                                <span className="text-muted">
                                  <i className="fa fa-users mr-2" />
                                  {primaryBatch.enrolledTrainees || 0} trainees
                                </span>

                                <Badge
                                  color={
                                    STATUS_BADGE_COLORS[
                                      primaryBatch?.batchStatus
                                    ] || "secondary"
                                  }
                                  pill
                                >
                                  {BATCH_STATUS_OPTIONS.find(
                                    (option) =>
                                      option.value ===
                                      primaryBatch?.batchStatus,
                                  )?.label ||
                                    primaryBatch?.batchStatus ||
                                    "-"}
                                </Badge>
                              </div>

                              <div className="assigned-batch-divider mt-2" />
                              <div className="text-muted small">
                                Enrollment Link
                              </div>
                              <div className="batch-meta-row mt-1 small">
                                <i className="fa fa-link mr-2" />

                                <span className="mr-2">Trainee:</span>

                                {copiedLink?.batchId ===
                                  primaryBatch?.batchId &&
                                copiedLink?.type === "trainee" ? (
                                  <span className="text-success font-weight-bold">
                                    <i className="fa fa-check mr-1" />
                                    Link Copied!
                                  </span>
                                ) : (
                                  <Button
                                    color="link"
                                    className="p-0 xxs-text"
                                    disabled={
                                      !primaryBatch?.batchEnrollmentURLTrainee
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      handleCopyEnrollmentLink(
                                        primaryBatch?.batchEnrollmentURLTrainee,
                                        primaryBatch?.batchId,
                                        "trainee",
                                      );
                                    }}
                                    title="Copy Trainee Enrollment Link"
                                  >
                                    <i className="fa fa-copy mr-1" />
                                    Copy Link
                                  </Button>
                                )}
                              </div>

                              <div className="batch-meta-row mt-1 small">
                                <i className="fa fa-link mr-2" />

                                <span className="mr-2">Channel Partner:</span>

                                {copiedLink?.batchId ===
                                  primaryBatch?.batchId &&
                                copiedLink?.type === "channelPartner" ? (
                                  <span className="text-success font-weight-bold">
                                    <i className="fa fa-check mr-1" />
                                    Link Copied!
                                  </span>
                                ) : (
                                  <Button
                                    color="link"
                                    className="p-0 xxs-text"
                                    disabled={
                                      !primaryBatch?.batchEnrollmentURLChannelPartner
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      handleCopyEnrollmentLink(
                                        primaryBatch?.batchEnrollmentURLChannelPartner,
                                        primaryBatch?.batchId,
                                        "channelPartner",
                                      );
                                    }}
                                    title="Copy Channel Partner Enrollment Link"
                                  >
                                    <i className="fa fa-copy mr-1" />
                                    Copy Link
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* <div className="assigned-batch-divider mt-2" /> */}
                          </CardBody>
                          <CardFooter>
                            <div
                              className="d-flex mt--2 mb--2 justify-content-between defalut-cursor"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div>
                                {remainingBatchCount > 0 && (
                                  <div
                                    className="more-batches-link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNavigation(course.courseId, true);
                                    }}
                                  >
                                    + {remainingBatchCount}{" "}
                                    {remainingBatchCount === 1
                                      ? "more batch"
                                      : "more batches"}
                                  </div>
                                )}
                              </div>
                              <div className="d-flex align-items-center text-right">
                                <Button
                                  color="link"
                                  className="p-0 mr-2"
                                  id={`view${course.courseId}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigation(course.courseId);
                                  }}
                                >
                                  <i className="fa fa-eye " />
                                </Button>
                                <UncontrolledTooltip
                                  delay={0}
                                  target={`view${course.courseId}`}
                                >
                                  View Course
                                </UncontrolledTooltip>
                              </div>
                            </div>
                          </CardFooter>
                        </Card>
                      </div>
                    );
                  })}
                </div>
                <CustomPagination
                  totalPages={totalPages}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                />
              </>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default InstructorCourses;
